import React, { useRef } from 'react';
import * as Sentry from '@sentry/react-native';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';

/**
 * VUELTO-NEGOCIO-2W: Max auto-recovery attempts for transient DOM errors
 * before falling back to the "Algo se rompió" UI. Prevents infinite loops
 * if the error keeps recurring after re-render.
 */
const MAX_DOM_RECOVERY_ATTEMPTS = 3;

/**
 * Detects transient React Native Web DOM errors that can be silently recovered.
 * The main case is `removeChild` on Node — caused by `detachInactiveScreens: true`
 * unmounting a Pressable with active press/hover state on web. Re-rendering
 * the children with fresh DOM state fixes it.
 */
function isRecoverableDomError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const name = error.name ?? '';
  const message = error.message ?? '';
  return name === 'NotFoundError' && message.includes('removeChild');
}

type SentryErrorBoundaryProps = {
  children: React.ReactNode;
  /** Module/screen name for tagging errors in Sentry */
  moduleName: string;
  /** Optional fallback render. Defaults to a simple "algo se rompió" UI. */
  fallback?: (error: unknown, resetError: () => void) => React.ReactElement;
};

const defaultFallback = (_error: unknown, resetError: () => void): React.ReactElement => (
  <View style={styles.container}>
    <Text style={styles.title}>Algo se rompió</Text>
    <Text style={styles.message}>Ya le avisamos al equipo. Probá volver atrás.</Text>
    <TouchableOpacity style={styles.button} onPress={resetError}>
      <Text style={styles.buttonText}>Reintentar</Text>
    </TouchableOpacity>
  </View>
);

export function SentryErrorBoundary({
  children,
  moduleName,
  fallback = defaultFallback,
}: SentryErrorBoundaryProps) {
  const domRecoveryAttempts = useRef(0);

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag('module', moduleName);
        scope.setContext('errorBoundary', { moduleName });
      }}
      fallback={({ error, resetError }) => {
        // VUELTO-NEGOCIO-2W: Silently recover from transient removeChild DOM
        // errors instead of showing "Algo se rompió". These are caused by
        // detachInactiveScreens unmounting Pressable components with active
        // press/hover state on web — re-rendering fixes it.
        if (isRecoverableDomError(error) && domRecoveryAttempts.current < MAX_DOM_RECOVERY_ATTEMPTS) {
          domRecoveryAttempts.current += 1;
          // Defer resetError to avoid calling setState during render phase
          setTimeout(() => resetError(), 0);
          return <></>;
        }
        domRecoveryAttempts.current = 0;
        return fallback(error, resetError);
      }}
      onError={(error) => {
        // VUELTO-NEGOCIO-2X: Don't report recoverable removeChild errors to
        // Sentry when we're going to silently recover. The recovery re-renders
        // with fresh DOM state and the user never sees the error. Only report
        // when recovery is exhausted (fallback UI shown) or the error is not
        // recoverable. A breadcrumb is left for context either way.
        const recoverable = isRecoverableDomError(error);
        const willRecover = recoverable && domRecoveryAttempts.current < MAX_DOM_RECOVERY_ATTEMPTS;
        if (willRecover) {
          Sentry.addBreadcrumb({
            level: 'info',
            category: 'errorBoundary',
            message: `recoverable removeChild DOM error in ${moduleName} — silent recovery attempted`,
          });
          return;
        }
        Sentry.captureException(error, {
          tags: {
            module: moduleName,
            ...(recoverable ? { domRecovery: 'exhausted' } : {}),
          },
        });
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    backgroundColor: '#fff',
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 8,
  },
  message: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginBottom: 20,
  },
  button: {
    backgroundColor: '#10b981',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  buttonText: {
    color: '#fff',
    fontWeight: '600',
  },
});
